import React, { useEffect, useState } from "react";
import { X, Users, AlertTriangle, CheckCircle2, RefreshCw, UserMinus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatTL, extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Lists prim rows whose giden personnel sit outside the current admin-defined
 * "Giden Kişi Kapsamı" scope. Powered by GET /api/prim/audit/giden-scope-mismatch.
 */
export default function GidenScopeAuditModal({ onClose, onFixed }) {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [scope, setScope] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/prim/audit/giden-scope-mismatch");
      setRows(r.data?.rows || []);
      setScope(r.data?.scope || []);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Yüklenemedi"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runFix = async (mode, primId = null) => {
    const q = primId
      ? (mode === "remove_offenders"
          ? "Bu prim satırından kapsam dışı giden(ler) çıkarılacak. Devam?"
          : "Bu satırın kapsam dışı pozisyonları scope'a eklenecek. Devam?")
      : (mode === "remove_offenders"
          ? `${rows.length} prim satırından kapsam dışı gidenler çıkarılacak. Devam?`
          : `Mevcut scope dışındaki tüm pozisyonlar scope'a eklenecek (kayıtlar olduğu gibi kalır). Devam?`);
    if (!window.confirm(q)) return;
    setFixing(true);
    try {
      const body = { mode };
      if (primId) body.prim_ids = [primId];
      const r = await api.post("/prim/audit/giden-scope-mismatch/fix", body);
      const d = r.data || {};
      if (mode === "remove_offenders") {
        toast.success(`${d.prims_updated || 0} prim güncellendi · ${d.giden_removed || 0} giden çıkarıldı`);
      } else {
        toast.success(`Scope +${d.positions_added || 0} pozisyon ile genişletildi`);
      }
      await load();
      onFixed && onFixed();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Düzeltilemedi"));
    } finally {
      setFixing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" data-testid="giden-scope-audit-modal">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 grid place-items-center">
              <AlertTriangle size={18} className="text-amber-600" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">Giden Kapsamı · Uyumsuzluk Raporu</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Mevcut giden kapsamı dışındaki pozisyonlarda personel ile oluşturulmuş prim satırları.
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid="giden-scope-audit-close" className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              data-testid="giden-scope-audit-count"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold border ${
                rows.length > 0
                  ? "bg-amber-50 border-amber-200 text-amber-700"
                  : "bg-emerald-50 border-emerald-200 text-emerald-700"
              }`}
            >
              {rows.length > 0 ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
              {rows.length === 0 ? "Uyumsuz kayıt yok" : `${rows.length} uyumsuz prim`}
            </div>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500">
              <Users size={12} /> Kapsam:
              {(scope || []).map((p) => (
                <span key={p} className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] font-bold ml-1">
                  {p}
                </span>
              ))}
            </span>
            <button
              onClick={load}
              disabled={loading}
              data-testid="giden-scope-audit-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Yenile
            </button>
          </div>
          {isAdmin && rows.length > 0 && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => runFix("remove_offenders")}
                disabled={fixing}
                data-testid="giden-scope-audit-remove-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold disabled:opacity-50"
                title="Scope dışındaki gidenleri her prim satırından çıkarır"
              >
                <UserMinus size={13} /> {fixing ? "..." : "Gidenleri Çıkar"}
              </button>
              <button
                onClick={() => runFix("extend_scope")}
                disabled={fixing}
                data-testid="giden-scope-audit-extend-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold disabled:opacity-50"
                title="Uyumsuz pozisyonları mevcut scope'a ekler (prim satırlarına dokunulmaz)"
              >
                <Plus size={13} /> {fixing ? "..." : "Scope'a Ekle"}
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto p-5">
          {loading && <div className="py-10 text-center text-slate-400 text-sm">Yükleniyor...</div>}
          {!loading && rows.length === 0 && (
            <div className="py-12 text-center">
              <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-2" />
              <div className="text-sm font-semibold text-slate-700">Harika — tüm giden kişiler mevcut kapsam içinde.</div>
            </div>
          )}
          {!loading && rows.length > 0 && (
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-xs" data-testid="giden-scope-audit-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-slate-600">
                    <th className="px-3 py-2 font-bold">Tarih</th>
                    <th className="px-3 py-2 font-bold">Müvekkil</th>
                    <th className="px-3 py-2 font-bold">Dosya / Esas</th>
                    <th className="px-3 py-2 font-bold text-right">Vekalet</th>
                    <th className="px-3 py-2 font-bold text-right">Tahsilat</th>
                    <th className="px-3 py-2 font-bold">Kapsam Dışı Giden(ler)</th>
                    {isAdmin && <th className="px-3 py-2 font-bold text-right">İşlem</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-amber-50/40">
                      <td className="px-3 py-2 font-mono-tab text-slate-600">{r.tarih || `${r.yil}-${r.ay}`}</td>
                      <td className="px-3 py-2 font-semibold text-slate-900">{r.alacakli_adi || "—"}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {r.dosya_adi || "—"}
                        <div className="text-[10px] text-slate-400 font-mono-tab">{[r.icra_foy, r.icra_esas].filter(Boolean).join(" / ")}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono-tab text-slate-700">{formatTL(r.vekalet_ucreti)}</td>
                      <td className="px-3 py-2 text-right font-mono-tab text-slate-700">{formatTL(r.tahsilat_miktari)}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(r.offenders || []).map((o) => (
                            <span
                              key={o.id}
                              data-testid={`giden-offender-${r.id}-${o.id}`}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-200"
                              title={`Pozisyon: ${o.pozisyon}`}
                            >
                              {o.ad_soyad || "(bilinmeyen)"}
                              {o.sicil_no && <span className="text-slate-500">· {o.sicil_no}</span>}
                              <span className="text-slate-500">·</span>
                              <span className="text-rose-700">{o.pozisyon}</span>
                            </span>
                          ))}
                        </div>
                      </td>
                      {isAdmin && (
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex gap-1">
                            <button
                              onClick={() => runFix("remove_offenders", r.id)}
                              disabled={fixing}
                              data-testid={`giden-row-remove-${r.id}`}
                              title="Bu satırdaki kapsam dışı gidenleri çıkar"
                              className="w-7 h-7 rounded text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center disabled:opacity-50"
                            >
                              <UserMinus size={13} />
                            </button>
                            <button
                              onClick={() => runFix("extend_scope", r.id)}
                              disabled={fixing}
                              data-testid={`giden-row-extend-${r.id}`}
                              title="Bu satırın kapsam dışı pozisyonlarını scope'a ekle"
                              className="w-7 h-7 rounded text-emerald-600 hover:bg-emerald-50 inline-flex items-center justify-center disabled:opacity-50"
                            >
                              <Plus size={13} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
